import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatTimeDuration } from '@racemap/utilities/formattings/times';
import { formatDistance } from '@racemap/utilities/formattings/units';
import {
  type PreparedRanksPackage,
  type PreparedStarterInfo,
  getVirtualRaceDistance,
  getVirtualRaceDuration,
  sortTimes,
  testEventHasVirtDuration,
} from '@racemap/utilities/functions/timing';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Splits } from '@racemap/utilities/types/types';
import { Unit } from '@racemap/utilities/types/units';
import { Progress } from 'antd';
import type { Immutable } from 'immer';
import type { ReactNode } from 'react';
import { Center } from '../../../BasicComponents/MetaComponent';
import type { Column } from '../TableBody';

function StageProgressColComponent({
  starterInfo,
  stage,
  splits,
}: {
  starterInfo: Immutable<PreparedStarterInfo>;
  stage: Immutable<RacemapEvent>;
  splits: Immutable<Splits>;
}) {
  const eventVirtualRaceDistance = getVirtualRaceDistance(stage, splits);
  const eventVirtualRaceDuration = getVirtualRaceDuration(stage);
  const eventHasVirtDuration = testEventHasVirtDuration(stage);
  const unitType = stage.playerOptions.unitType;
  const now =
    Math.floor(
      (starterInfo?.progress /
        (eventHasVirtDuration ? eventVirtualRaceDuration : eventVirtualRaceDistance)) *
        100000,
    ) / 1000;
  if (starterInfo.progress == null) return <></>;

  return (
    <Cell>
      <RankAndProgressValue>
        <div>
          {eventHasVirtDuration
            ? starterInfo.progress === -1
              ? '00:00:00'
              : formatTimeDuration(starterInfo?.progress)
            : formatDistance({
                value:
                  starterInfo != null
                    ? starterInfo.progress === -1
                      ? 0
                      : starterInfo.progress
                    : 0,
                inputUnit: Unit.Distance.m,
                unitType,
              })}
        </div>
        <Rank>({starterInfo.rank})</Rank>
      </RankAndProgressValue>

      <ProgressContainer>
        {now > 0 && <Progress percent={now} status={now >= 100 ? 'success' : undefined} />}
      </ProgressContainer>
    </Cell>
  );
}

export function getStageProgressCol(
  stageEvent: Immutable<RacemapEvent>,
  splits: Immutable<Splits>,
  stageStarterData: Immutable<PreparedRanksPackage>,
): Column {
  const eventHasVirtDuration = testEventHasVirtDuration(stageEvent);
  const starterResults = new Map(stageStarterData.starters.map((s) => [s.id, s]));

  return {
    key: `stage_progress-${stageEvent.id}`,
    title: <HeaderCell>{stageEvent.name}</HeaderCell>,
    width: 140,
    render: function renderer(row: Immutable<PreparedStarterInfo>): ReactNode {
      const starterInfo = starterResults.get(row.id);
      if (starterInfo == null) return '--';

      return (
        <StageProgressColComponent starterInfo={starterInfo} stage={stageEvent} splits={splits} />
      );
    },
    sorter: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) => {
      const resA = starterResults.get(a.id);
      const resB = starterResults.get(b.id);
      const progressA = resA?.progress || -1;
      const progressB = resB?.progress || -1;

      return eventHasVirtDuration ? sortTimes(progressB, progressA) : progressB - progressA;
    },
  };
}

const Cell = styled(Center)`
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  width: 90%;
  height: 15px;
`;

const RankAndProgressValue = styled.div`
  display: flex;
  flex-direction: row;
`;

const Rank = styled.div`
  margin-left: 5px;
  color: ${RacemapColors.DarkGray};
`;

const HeaderCell = styled(Center)`
  width: 100%;
  font-weight: 600;
`;
